import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BRAND } from '@/lib/brand';
import { toast } from 'sonner';
import { Loader2, Globe, ShieldCheck, Activity, Database } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('admin@tuiglesia.app');
  const [password, setPassword] = React.useState('Admin123!');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

  const switchLang = (l) => { i18n.changeLanguage(l); localStorage.setItem('lang', l); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success(t('welcome_back'));
      navigate('/');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 bomm-cyan-wash pointer-events-none" />
        <div className="absolute inset-0 bomm-noise opacity-40 pointer-events-none" />
        <div className="relative">
          <img src={BRAND.logoWhite} alt={BRAND.name} className="h-14 w-14 object-contain" />
          <h1 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight mt-8 max-w-lg">
            {BRAND.product}
            <span className="text-[hsl(var(--primary))]"> •</span>
          </h1>
          <h2 className="font-display text-xl xl:text-2xl text-foreground/90 mt-2 max-w-lg">
            {t('product_subtitle')}
          </h2>
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-md">
            {i18n.language === 'en' ? BRAND.tagline_en : BRAND.tagline_es}
          </p>
          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3">
              <Activity className="h-4 w-4 text-[hsl(var(--primary))]" />
              <span>{i18n.language === 'en' ? 'Real-time service health' : 'Salud de servicios en tiempo real'}</span>
            </li>
            <li className="flex items-center gap-3">
              <Database className="h-4 w-4 text-[hsl(var(--primary))]" />
              <span>{i18n.language === 'en' ? 'Database stats & explorer' : 'Stats y explorer de la BD'}</span>
            </li>
            <li className="flex items-center gap-3">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />
              <span>{i18n.language === 'en' ? 'Role-based access (Admin/Tech/Viewer)' : 'Acceso por rol (Admin/Técnico/Viewer)'}</span>
            </li>
          </ul>
        </div>
        <div className="relative text-xs text-[hsl(var(--muted-foreground))]">
          © {new Date().getFullYear()} {BRAND.name} • {BRAND.product}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-[0_1px_0_rgba(255,255,255,0.06),0_18px_50px_rgba(0,0,0,0.45)]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoWhite} alt={BRAND.name} className="h-10 w-10 object-contain lg:hidden" />
              <div>
                <div className="font-display text-xl font-semibold">{t('welcome_back')}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{t('login_subtitle')}</div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="login-language-toggle">
                  <Globe className="h-4 w-4 mr-1" /> <span className="text-xs uppercase">{i18n.language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => switchLang('es')}>Español</DropdownMenuItem>
                <DropdownMenuItem onClick={() => switchLang('en')}>English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@tuiglesia.app"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full active:scale-[0.98] transition-transform duration-150"
                    disabled={submitting} data-testid="login-submit-button">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('loading')}</> : t('sign_in')}
            </Button>
          </form>

          <div className="mt-6 rounded-lg border border-border bg-muted p-3 text-xs">
            <div className="text-[hsl(var(--muted-foreground))] uppercase tracking-wide text-[10px] mb-2">{t('sample_credentials')}</div>
            <ul className="space-y-1 font-mono text-[11px]">
              <li><span className="text-[hsl(var(--primary))]">admin@tuiglesia.app</span> / Admin123!</li>
              <li><span className="text-[hsl(var(--primary))]">tecnico@tuiglesia.app</span> / Tecnico123!</li>
              <li><span className="text-[hsl(var(--primary))]">viewer@tuiglesia.app</span> / Viewer123!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
